import Close from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GetAppIcon from "@mui/icons-material/GetApp";
import ReplayIcon from "@mui/icons-material/Replay";
import Visibility from "@mui/icons-material/Visibility";
import WarningTwoToneIcon from "@mui/icons-material/WarningTwoTone";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  IconButton,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { MaterialReactTable } from "material-react-table";
import moment from "moment";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getConfidentialFields } from "../../../actions/fields";
import { auditarExportacion, CODIGO_AUDITORIA } from "../../../actions/audit";
import { getFormatoFechaExportacion } from "../../../utils/datetimeUtils";
import { downloadCsv } from "../../../utils/filesUtils";
import snackbarUtils from "../../../utils/snackbarUtils";
import { redactFromRegistroConfidentialFields } from "../../../utils/util";
import { StyledAccident } from "../../styled/accident";
import { StyledDatesChips } from "../../styled/dates";
import MessagesTooltip from "../tooltip/messagesTooltip";
import { tableConfig } from "./config/tableConfig";
import DetailsTable from "./utilsTable/detailsTableAccidents";
import TitleContainer from "./utilsTable/titleWithFetchData";

const AccidentTable = ({
  data,
  namesArts,
  loading,
  fetchData,
  handleAccidentDetail,
  handleEdit,
  handleReOpenAccident,
  displayAccidents,
  openModalCloseAccident,
  noRefresh,
  openInSearch,
  showArtColumn,
}) => {
  const dispatch = useDispatch();
  const esPrestador = useSelector((state) => state.auth.roles?.PRESTADOR);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  const columns = [
    ...(!showArtColumn
      ? [
        {
          header: "Art",
          accessorKey: "art",
          enableColumnActions: false,
          enableFiltering: true,
          size: 100,
          maxSize: 100,

          Cell: ({ row }) => row.original.art.toUpperCase(),
          Filter: ({ column }) => (
            <Autocomplete
              multiple
              options={Object.values(namesArts ?? [])}
              value={column.getFilterValue() || []}
              onChange={(event, newValue) =>
                column.setFilterValue(newValue.length ? newValue : undefined)
              }
              getOptionLabel={(option) => option.nombreCorto}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      minHeight: 35,
                    },
                    "& .MuiInputAdornment-root": {
                      height: 30,
                    },
                    "& .MuiOutlinedInput-input ": {
                      padding: "5px 10px",
                      fontSize: "0.875rem",
                    },
                    "& .MuiChip-root": {
                      height: 20,
                      fontSize: "0.75rem",
                      margin: "2px",
                    },
                    "& .MuiAutocomplete-input": {
                      padding: "5px 10px",
                      fontSize: "0.875rem",
                    },
                    "& .MuiAutocomplete-popover": {
                      maxHeight: 250,
                      overflowY: "auto",
                    },
                  }}
                />
              )}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <ListItem {...props} key={option.id}>
                  <Checkbox
                    size="sm"
                    checked={selected}
                    sx={{
                      padding: 0,
                      margin: 0,
                      marginRight: 0.5,
                      marginLeft: -1,
                    }}
                  />
                  <Typography variant="body2">
                    {option.nombreCorto}
                  </Typography>
                </ListItem>
              )}
            />
          ),
          filterFn: (row, id, filterValue) => {
            if (!filterValue || filterValue.length === 0) return true; // Mostrar todos si no hay filtro
            const rowValue = row.original.mlCloudCoreArtId; // Convertimos a mayúsculas para coincidir con `Cell`
            return filterValue.some((value) => rowValue === value.id);
          },
        },
      ]
      : []), // Incluye o excluye la columna "Art"
    {
      header: "Nuevo",
      accessorKey: "nuevo",
      placeholder: "",
      enableSorting: false,
      enableColumnActions: false,
      Cell: ({ row }) => (row.original.nuevo ? "Sí" : "No"),
      Filter: ({ column }) => (
        <Select
          variant="standard"
          fullWidth
          sx={{
            minHeight: 35,
            textAlign: "start",
          }}
          value={column.getFilterValue() || "Todos"}
          onChange={(e) =>
            column.setFilterValue(
              e.target.value === "Todos" ? undefined : e.target.value,
            )
          }
          displayEmpty>
          <MenuItem value="Todos">Todos</MenuItem>
          <MenuItem value="true">Sí</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </Select>
      ),
      filterFn: (row, id, filterValue) => {
        const value = row.getValue(id);
        if (filterValue === "Todos") return true; // No filtra si se selecciona "Todos"
        return filterValue === "true" ? value === true : value === false;
      },
    },
    {
      header: "Trabajador",
      accessorFn: (row) =>
        `${row.apellidoTrabajador || ""}, ${row.nombreTrabajador || ""}`,
      enableColumnActions: false,

      filterFn: (row, columnId, filterValue) => {
        const fullName = `${row.original.apellidoTrabajador || ""}, ${row.original.nombreTrabajador || ""
          }`.toLowerCase();

        return fullName.includes(filterValue.toLowerCase());
      },
      Cell: ({ row }) =>
        `${row.original.apellidoTrabajador}, ${row.original.nombreTrabajador}`,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              height: 35,
              minHeight: "unset",
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
          placeholder=""
        />
      ),
    },
    {
      header: "Siniestro",
      accessorKey: "siniestro",
      enableColumnActions: false,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              height: 35,
              minHeight: "unset",
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
          placeholder=""
        />
      ),
      Cell: ({ row }) =>
        row.original.siniestro === "0" ? " " : row.original.siniestro,
    },
    {
      header: "Documento",
      accessorKey: "documento",
      enableColumnActions: false,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              height: 35,
              minHeight: "unset",
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
          placeholder=""
        />
      ),
      filterFn: "contains",
    },
    {
      header: "Accidente",
      accessorKey: "fechaHoraAccidente",
      enableColumnActions: false,
      Filter: ({ column }) => {
        const [selectedDate, setSelectedDate] = React.useState(null);

        return (
          <DatePicker
            value={selectedDate}
            onChange={(newValue) => {
              setSelectedDate(newValue);
              column.setFilterValue(
                newValue ? moment(newValue).format("YYYY-MM-DD") : undefined,
              );
            }}
            slotProps={{
              textField: {
                variant: "standard",
                sx: {
                  "& .MuiInputBase-root": {
                    height: 35,
                    minHeight: "unset",
                  },
                  "& .MuiOutlinedInput-input ": {
                    height: 5,
                  },
                  "& .MuiChip-root": {
                    height: 1,
                  },
                },
              },
            }}
            renderInput={(params) => <TextField fullWidth {...params} />}
          />
        );
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true; // Mostrar todas las filas si no hay filtro activo
        const cellValue = row.getValue(columnId); // Obtener el valor de la celda
        if (!cellValue) return false; // Excluir filas sin fecha si hay filtro
        return moment(cellValue).isSame(filterValue, "day"); // Verificar coincidencia exacta
      },
      Cell: ({ row }) =>
        moment(row.original.fechaHoraAccidente).isValid()
          ? moment(row.original.fechaHoraAccidente).format("DD/MM/YYYY")
          : "",
    },
    {
      header: "Próximo Control",
      accessorKey: "proximoControl",
      enableColumnActions: false,
      Filter: ({ column }) => {
        const handleChange = (event, newValue) => {
          if (!newValue || newValue.length === 0) {
            column.setFilterValue(undefined);
            return;
          }

          // Obtener el último elemento agregado
          const lastAdded = newValue[newValue.length - 1];

          // Filtrar: mantener solo un elemento de cada tipo
          let filteredValue = newValue.filter((item, index) => {
            // Si es el último agregado, siempre lo mantenemos
            if (index === newValue.length - 1) return true;
            // Si es de tipo diferente al último agregado, lo mantenemos
            return item.type !== lastAdded.type;
          });

          column.setFilterValue(filteredValue.length ? filteredValue : undefined);
        };

        return (
          <Autocomplete
            multiple
            options={optionsProximoControl}
            value={column.getFilterValue() || []}
            onChange={handleChange}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    height: 35,
                    minHeight: "unset",
                  },
                  "& .MuiInputAdornment-root": {
                    height: 30,
                  },
                  "& .MuiOutlinedInput-input ": {
                    padding: "5px 10px",
                    fontSize: "0.875rem",
                  },
                  "& .MuiChip-root": {
                    height: 20,
                    fontSize: "0.75rem",
                    margin: "2px",
                  },
                  "& .MuiAutocomplete-input": {
                    padding: "5px 10px",
                    fontSize: "0.875rem",
                  },
                  "& .MuiAutocomplete-popover": {
                    maxHeight: 250,
                    overflowY: "auto",
                  },
                }}
              />
            )}
            disableCloseOnSelect
            renderOption={(props, option, { selected }, state) => {
              const currentIndex = optionsProximoControl.findIndex(opt => opt.value === option.value);
              const previousOption = currentIndex > 0 ? optionsProximoControl[currentIndex - 1] : null;
              const showDivider = (previousOption && previousOption.type === "futuro" && option.type === "pasado") ||
                (previousOption && previousOption.type === "especial" && option.type === "futuro");

              return (
                <React.Fragment key={option.value}>
                  {showDivider && (
                    <Box sx={{
                      borderTop: "1px solid #e0e0e0",
                      my: 0.5,
                      width: "100%"
                    }} />
                  )}
                  <ListItem {...props}>
                    <Checkbox
                      size="sm"
                      checked={selected}
                      sx={{
                        padding: 0,
                        margin: 0,
                        marginRight: 0.5,
                        marginLeft: -1,
                      }}
                    />
                    <Typography variant="body2">{option.label}</Typography>
                  </ListItem>
                </React.Fragment>
              );
            }}
          />
        );
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue.length === 0) return true;

        const rowValue = row.getValue(columnId);

        // Verificar "Sin próximo control"
        const hasSinProximoControl = filterValue.some(f => f.value === "SinProximoControl");
        if (hasSinProximoControl && rowValue === null) return true;

        if (rowValue === null) return false;

        // Verificar si cae en alguno de los rangos seleccionados
        return filterValue.some(filter => {
          if (filter.value === "SinProximoControl") return false;

          if (filter.value > 0) {
            return moment(rowValue).isBetween(
              moment(),
              moment().add(filter.value, "hours"),
              undefined,
              "[]"
            );
          } else {
            return moment(rowValue).isBetween(
              moment().subtract(Math.abs(filter.value), "hours"),
              moment(),
              undefined,
              "[]"
            );
          }
        });
      },
      Cell: ({ row }) => {
        const date = row.original.proximoControl;
        const diff = date ? moment(date).diff(Date.now(), "days") : null;
        return date ? (
          <Chip
            label={moment(date).format("DD/MM/YYYY")}
            sx={
              diff < 0 ? StyledDatesChips.errorChip : StyledDatesChips.successChip
            }
          />
        ) : (
          <></> // Retornamos un elemento vacío si no hay fecha
        );
      },
    },
    {
      header: "Desvío",
      accessorKey: "desvio",
      enableSorting: false,
      Filter: ({ column }) => (
        <Select
          fullWidth
          variant="standard"
          sx={{
            textAlign: "start",
            minHeight: 35,
            margin: 0,
          }}
          value={column.getFilterValue() || "Todos"}
          onChange={(e) =>
            column.setFilterValue(
              e.target.value === "Todos" ? undefined : e.target.value,
            )
          }
          displayEmpty>
          <MenuItem value="Todos">Todos</MenuItem>
          <MenuItem value="true">Sí</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </Select>
      ),
      filterFn: (row, id, filterValue) => {
        const value = row.getValue(id);
        if (filterValue === "Todos") return true; // No filtra si se selecciona "Todos"
        return filterValue === "true" ? value === true : value === false;
      },
      enableColumnActions: false,
      Cell: ({ row }) =>
        row.original.desvio ? (
          <MessagesTooltip values={row.original.desviosDescripcion}>
            <WarningTwoToneIcon
              sx={{
                fill: "#e6d400",
                width: 30,
                height: 30,
                cursor: "pointer",
              }}
            />
          </MessagesTooltip>
        ) : (
          ""
        ),
    },
    {
      header: "Acciones",
      accessorKey: "acciones",
      enableColumnFilter: false,
      enableColumnActions: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <StyledAccident.TableActions>
          {/* Acción Editar */}
          <Tooltip
            title={
              row.original.editable
                ? "Editar"
                : "No es posible editar el siniestro"
            }>
            <IconButton
              onClick={() => handleEdit(row.original)}
              size="small"
              color="inherit"
              disabled={!row.original.editable || !esPrestador}>
              <EditOutlinedIcon />
            </IconButton>
          </Tooltip>

          {/* Acción Reabrir Siniestro */}
          {!openInSearch ? null : (
            <Tooltip title="Reabrir Siniestro">
              <IconButton
                onClick={() => handleReOpenAccident(row.original)}
                size="small"
                color="inherit"
                disabled={!row.original.reabrirSiniestro || !esPrestador}>
                <ReplayIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Acción Ver Detalles */}
          <Tooltip title="Ver Detalles">
            <IconButton
              onClick={() => handleAccidentDetail(row.original)}
              size="small"
              color="inherit">
              <Visibility />
            </IconButton>
          </Tooltip>

          {/* Acción Cerrar Siniestro */}
          {!openInSearch ? (
            <Tooltip title="Cerrar Siniestro">
              <IconButton
                onClick={() => openModalCloseAccident(row.original)}
                size="small"
                color="inherit"
                disabled={
                  !(
                    row.original.cerrarSiniestro === true &&
                    row.original.alta === false &&
                    row.original.cerrado === 0
                  ) && esPrestador
                }>
                <Close />
              </IconButton>
            </Tooltip>
          ) : null}
        </StyledAccident.TableActions>
      ),
    },
  ];

  // Manejo de exportación
  const handleExportToCsv = () => {
    setIsDownloadingCsv(true);
    dispatch(auditarExportacion(data, "mlCloudCoreArtId", CODIGO_AUDITORIA.EXPORTACION_SINIESTROS))
      .then(() => dispatch(getConfidentialFields("siniestro")))
      .then((res) => {
        const redactedData = data.map((row) =>
          redactFromRegistroConfidentialFields(
            row,
            res.value.data,
            "mlCloudCoreArtId",
          ),
        );

        const encabezado = [
          "Art",
          "Siniestro",
          "Documento",
          "Trabajador",
          "Empleador",
          "Accidente",
          "Próximo Control",
          "Administrador",
          "CuitEmpleador",
          "Alta",
          "Primera Curación",
          "Estado",
        ];

        const formattedDataAsCsv = [
          encabezado,
          ...redactedData.map((fila) => [
            fila.art,
            fila.siniestro,
            fila.documento,
            `${fila.nombreTrabajador} ${fila.apellidoTrabajador}`,
            fila.empleador,
            getFormatoFechaExportacion(fila.fechaHoraAccidente, "P"),
            getFormatoFechaExportacion(fila.proximoControl, "P"),
            fila.administrador,
            fila.cuitEmpleador,
            getFormatoFechaExportacion(fila.fechaAlta, "P"),
            getFormatoFechaExportacion(fila.fechaPrimeraCuracion, "P"),
            fila.estado,
          ]),
        ];

        let csvContent = "sep=,\n";
        csvContent += formattedDataAsCsv.map((e) => e.join(",")).join("\n");

        downloadCsv(csvContent, "Accidentes");
        snackbarUtils.success("Los siniestros han sido descargados con éxito.");
        setIsDownloadingCsv(false);
      })
      .catch((err) => {
        snackbarUtils.error(err);
        setIsDownloadingCsv(false);
      });
  };

  // Opciones Próximo Control (separadas por tipo para restricción de selección)
  const optionsProximoControl = [
    { value: "SinProximoControl", label: "Sin próximo control", type: "especial" },
    { value: 24, label: "Próximas 24hs", type: "futuro" },
    { value: 48, label: "Próximas 48hs", type: "futuro" },
    { value: 72, label: "Próximas 72hs", type: "futuro" },
    { value: 168, label: "Próximos 7 días", type: "futuro" },
    { value: -24, label: "Últimas 24hs", type: "pasado" },
    { value: -48, label: "Últimas 48hs", type: "pasado" },
    { value: -72, label: "Últimas 72hs", type: "pasado" },
    { value: -168, label: "Últimos 7 días", type: "pasado" },
  ];

  return (
    <>
      {displayAccidents !== "block" ? (
        <></>
      ) : (
        <MaterialReactTable
          {...tableConfig}
          enableColumnFilters
          enableExpandAll={false}
          columns={columns}
          renderTopToolbarCustomActions={() => (
            <Box m={0.5} display="flex" justifyContent="space-evenly">
              <TitleContainer
                title={"Siniestros"}
                noRefresh={noRefresh}
                fetchData={fetchData}
              />
              <Tooltip title="Exportar CSV" arrow="top">
                <IconButton
                  color="inherit"
                  sx={{ margin: 2 }}
                  onClick={handleExportToCsv}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          data={data}
          state={{ isLoading: loading || isDownloadingCsv }}
          muiTableHeadCellProps={{
            sx: {
              ...tableConfig.muiTableHeadCellProps.sx,
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          }}
          muiTableHeadProps={{
            sx: {
              marginBottom: 5,
            },
          }}
          defaultColumn={{
            maxSize: 50,
            grow: false,
          }}
          renderDetailPanel={({ row }) => {
            return <DetailsTable rowData={row.original} />;
          }}
        />
      )}
    </>
  );
};

export default AccidentTable;
